import { useState, useMemo } from "react";
import { resumoCta } from "@/mocks/cta";
import { useTreesList } from "@/hooks";
import TreeCard from "./components/TreeCard";
import TreeTable from "./components/TreeTable";
import TreeFilters from "./components/TreeFilters";
import TreeQualityPanel from "./components/TreeQualityPanel";
import TreePriorityPanel from "./components/TreePriorityPanel";
import PageHeader from "@/components/shared/PageHeader";
import KpiCard from "@/components/shared/KpiCard";
import EmptyState from "@/components/shared/EmptyState";

export default function CTA() {
  const [viewMode, setViewMode] = useState<"cards" | "table">("table");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const { items: individuos } = useTreesList();

  const filtered = useMemo(() => {
    return individuos.filter((ind) => {
      if (filters.search) {
        const s = filters.search;
        if (
          !ind.codigoCta.toLowerCase().includes(s) &&
          !ind.especie.toLowerCase().includes(s) &&
          !ind.nomePopular.toLowerCase().includes(s) &&
          !ind.nomeCientifico.toLowerCase().includes(s) &&
          !ind.logradouro.toLowerCase().includes(s) &&
          !ind.bairro.toLowerCase().includes(s)
        ) return false;
      }
      if (filters.tipo && ind.tipo !== filters.tipo) return false;
      if (filters.fito && ind.estadoFitossanitario !== filters.fito) return false;
      if (filters.risco && ind.classeRisco !== filters.risco) return false;
      if (filters.recomendacao && ind.recomendacao !== filters.recomendacao) return false;
      if (filters.statusRevisao && ind.statusRevisao !== filters.statusRevisao) return false;
      return true;
    });
  }, [filters, individuos]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="CTA Arbóreo"
        subtitle="Cadastro Técnico Arbóreo com indivíduos georreferenciados, fichas técnicas, risco, vistorias e histórico."
      >
        <button className="btn-outline text-xs px-3 py-2 cursor-pointer">
          <i className="ri-download-line"></i>
          <span className="hidden sm:inline">Exportar</span>
        </button>
        <button className="btn-outline text-xs px-3 py-2 cursor-pointer">
          <i className="ri-map-2-line"></i>
          <span className="hidden sm:inline">Abrir Mapa</span>
        </button>
        <button className="btn-outline text-xs px-3 py-2 cursor-pointer">
          <i className="ri-upload-cloud-line"></i>
          <span className="hidden sm:inline">Importar Planilha</span>
        </button>
        <button className="btn-primary text-xs px-3 py-2 cursor-pointer">
          <i className="ri-add-line"></i>
          <span className="hidden sm:inline">Nova Árvore</span>
        </button>
      </PageHeader>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <KpiCard label="Total de Indivíduos" value={resumoCta.totalIndividuos} icon="ri-plant-line" variant="success" />
        <KpiCard label="Validadas" value={resumoCta.validadas} icon="ri-check-double-line" variant="success" />
        <KpiCard label="Pendentes Revisão" value={resumoCta.pendentesRevisao} icon="ri-edit-line" variant="warning" />
        <KpiCard label="Risco Crítico" value={resumoCta.riscoCritico} icon="ri-alert-line" variant="danger" />
        <KpiCard label="Poda Recomendada" value={resumoCta.podaRecomendada} icon="ri-scissors-cut-line" variant="warning" />
        <KpiCard label="Supressão" value={resumoCta.supressaoRecomendada} icon="ri-close-circle-line" variant="danger" />
        <KpiCard label="Espécies Identif." value={resumoCta.especiesIdentificadas} icon="ri-leaf-line" variant="info" />
        <KpiCard label="Fotos Anexadas" value={resumoCta.fotosAnexadas.toLocaleString("pt-BR")} icon="ri-camera-line" variant="default" />
      </div>

      {/* Filters */}
      <TreeFilters onFilter={setFilters} />

      {/* View Toggle + Results */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground-500">
          {filtered.length} {filtered.length === 1 ? "indivíduo" : "indivíduos"} encontrado{filtered.length !== 1 ? "s" : ""}
          {Object.keys(filters).length > 0 && " (com filtros aplicados)"}
        </p>
        <div className="flex items-center bg-white rounded-lg border border-background-200/70 p-0.5">
          <button
            onClick={() => setViewMode("table")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
              viewMode === "table" ? "bg-primary-50 text-primary-700" : "text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <i className="ri-table-line mr-1"></i>Tabela
          </button>
          <button
            onClick={() => setViewMode("cards")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
              viewMode === "cards" ? "bg-primary-50 text-primary-700" : "text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <i className="ri-grid-line mr-1"></i>Cards
          </button>
        </div>
      </div>

      {/* Main Content */}
      {filtered.length > 0 ? (
        viewMode === "table" ? (
          <TreeTable individuos={filtered} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((ind) => (
              <TreeCard key={ind.id} individuo={ind} />
            ))}
          </div>
        )
      ) : (
        <EmptyState
          icon="ri-plant-line"
          title="Nenhum indivíduo encontrado"
          description="Ajuste os filtros para ver mais resultados."
        />
      )}

      {/* Priority + Quality Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <TreePriorityPanel />
        </div>
        <div>
          <TreeQualityPanel />
        </div>
      </div>
    </div>
  );
}